import { useStore } from 'vuex'
import { computed, getCurrentInstance } from 'vue'
import workflowApi from '@/api/workflowApi'
import safeStructuredClone from '@/utils/clone'
import usePrompts from '@/composables/usePrompts'

const useEntityActions = () => {
  const store = useStore()
  const { proxy } = getCurrentInstance()
  const { getDataFieldExampleData } = usePrompts()
  const workflow = computed(() => store.state.workflow.workflow)

  const moveRight = async (entityIndex, fieldIndex) => {
    if (workflow.value.dataModels[entityIndex].dataFields.length > (fieldIndex + 1)) {
      const newDataModels = safeStructuredClone(workflow.value.dataModels || [])
      const field = newDataModels[entityIndex].dataFields[fieldIndex]

      newDataModels[entityIndex].dataFields[fieldIndex] = newDataModels[entityIndex].dataFields[fieldIndex + 1]
      newDataModels[entityIndex].dataFields[fieldIndex + 1] = field
      await workflowApi.updateDataModels({
        dataModelsOnWorkflow: newDataModels,
        workflow: workflow.value
      })
    }
  }

  const moveLeft = async (entityIndex, fieldIndex) => {
    if (fieldIndex >= 1) {
      const newDataModels = safeStructuredClone(workflow.value.dataModels || [])
      const field = newDataModels[entityIndex].dataFields[fieldIndex]
      newDataModels[entityIndex].dataFields[fieldIndex] = newDataModels[entityIndex].dataFields[fieldIndex - 1]
      newDataModels[entityIndex].dataFields[fieldIndex - 1] = field

      await workflowApi.updateDataModels({
        dataModelsOnWorkflow: newDataModels,
        workflow: workflow.value
      })
    }
  }

  const blurEntityName = async (e, entityIndex) => {
    const entities = safeStructuredClone(workflow.value.dataModels || [])

    if (entities[entityIndex].name !== e.target.innerText) {
      entities[entityIndex].name = e.target.innerText
      await workflowApi.updateDataModels({
        dataModelsOnWorkflow: entities,
        workflow: workflow.value
      })
    }
  }

  const blurTableCell = async (e, entityIndex, fieldIndex, fieldName, indexAttribute = -1) => {
    let oldVal
    const newVal = e.target.innerText?.trim() ?? ''
    if (indexAttribute !== null && indexAttribute >= 0) {
      oldVal = workflow.value.dataModels[entityIndex].dataFields[fieldIndex][fieldName][indexAttribute]
    } else {
      oldVal = workflow.value.dataModels[entityIndex].dataFields[fieldIndex][fieldName]
    }
    if (oldVal === undefined && newVal === '') {
      return
    }
    const newDataModels = safeStructuredClone(workflow.value.dataModels || [])
    if (oldVal !== newVal) {
      if (indexAttribute !== null && indexAttribute >= 0) {
        newDataModels[entityIndex].dataFields[fieldIndex][fieldName][indexAttribute] = newVal
      } else {
        newDataModels[entityIndex].dataFields[fieldIndex][fieldName] = newVal
      }
      await workflowApi.updateDataModels({
        dataModelsOnWorkflow: newDataModels,
        workflow: workflow.value
      })
    }
  }

  const addColumn = async (entityIndex) => {
    const newDataModels = safeStructuredClone(workflow.value.dataModels || [])
    newDataModels[entityIndex].dataFields.push({
      name: 'NEW FIELD ' + (workflow.value.dataModels[entityIndex].dataFields.length + 1),
      cardinality: 'one-to-one',
      exampleData: ['']
    })
    await workflowApi.updateDataModels({
      dataModelsOnWorkflow: newDataModels,
      workflow: workflow.value
    })
  }

  const deleteEntity = async (entityIndex) => {
    const newDataModels = safeStructuredClone(workflow.value.dataModels || [])
    newDataModels.splice(entityIndex, 1)

    await workflowApi.updateDataModels({
      dataModelsOnWorkflow: newDataModels,
      workflow: workflow.value
    })
  }

  const deleteEntityField = async (entityIndex, fieldIndex) => {
    const newDataModels = safeStructuredClone(workflow.value.dataModels || [])
    newDataModels[entityIndex].dataFields.splice(fieldIndex, 1)

    await workflowApi.updateDataModels({
      dataModelsOnWorkflow: newDataModels,
      workflow: workflow.value
    })
  }

  const selectRelatedEntity = async (relatedEntity, entityIndex, fieldIndex) => {
    const newDataModels = safeStructuredClone(workflow.value.dataModels || [])
    newDataModels[entityIndex].dataFields[fieldIndex].relatedEntityId = relatedEntity?.id || null

    if (!newDataModels[entityIndex].dataFields[fieldIndex].cardinality) {
      newDataModels[entityIndex].dataFields[fieldIndex].cardinality = 'one-to-one'
    }

    await workflowApi.updateDataModels({
      dataModelsOnWorkflow: newDataModels,
      workflow: workflow.value
    })
  }

  const selectCardinality = async (cardinality, entityIndex, entityId) => {
    const newDataModels = safeStructuredClone(workflow.value.dataModels || [])

    newDataModels[entityIndex].dataFields.forEach((dataField, index) => {
      if (dataField.relatedEntityId === entityId) {
        newDataModels[entityIndex].dataFields[index].cardinality = cardinality || null
      }
    })

    await workflowApi.updateDataModels({
      dataModelsOnWorkflow: newDataModels,
      workflow: workflow.value
    })
  }

  const setPrimaryKey = async (entityIndex, fieldIndex) => {
    const newDataModels = safeStructuredClone(workflow.value.dataModels || [])

    newDataModels[entityIndex].dataFields = newDataModels[entityIndex].dataFields.map((field, index) => {
      if (index === fieldIndex) {
        return { ...field, primaryKey: !field.primaryKey, reference: !field.primaryKey ? false : field.reference }
      }

      return { ...field, primaryKey: false }
    })

    await workflowApi.updateDataModels({
      dataModelsOnWorkflow: newDataModels,
      workflow: workflow.value
    })
  }

  const setReference = async (entityIndex, fieldIndex) => {
    const newDataModels = safeStructuredClone(workflow.value.dataModels || [])
    const dataField = newDataModels[entityIndex].dataFields[fieldIndex]

    dataField.reference = !dataField.reference

    if (dataField.reference && dataField.primaryKey) {
      dataField.primaryKey = false
    }

    await workflowApi.updateDataModels({
      dataModelsOnWorkflow: newDataModels,
      workflow: workflow.value
    })
  }

  const setFieldMandatory = async (entityIndex, fieldIndex, isMandatory) => {
    const newDataModels = safeStructuredClone(workflow.value.dataModels || [])

    newDataModels[entityIndex].dataFields = newDataModels[entityIndex].dataFields.map((field, index) => {
      if (index === fieldIndex) {
        return { ...field, tags: isMandatory ? ['REQUIRED'] : [] }
      }

      return field
    })

    await workflowApi.updateDataModels({
      dataModelsOnWorkflow: newDataModels,
      workflow: workflow.value
    })
  }

  const generateExampleData = async (entityIndex, dataFieldIndex) => {
    const entity = workflow.value.dataModels[entityIndex]

    if (!entity || !entity.dataFields) {
      return
    }

    proxy.$loadingState.start()
    const dataField = entity.dataFields[dataFieldIndex]

    const response = await getDataFieldExampleData(entity, dataField)

    if (response.exampleData) {
      const newDataModels = safeStructuredClone(workflow.value.dataModels || [])
      newDataModels[entityIndex].dataFields[dataFieldIndex].exampleData = response.exampleData

      await workflowApi.updateDataModels({
        dataModelsOnWorkflow: newDataModels,
        workflow: workflow.value
      })
    }
    proxy.$loadingState.stop()
  }

  return {
    moveRight,
    moveLeft,
    blurEntityName,
    blurTableCell,
    addColumn,
    deleteEntity,
    deleteEntityField,
    selectRelatedEntity,
    selectCardinality,
    setPrimaryKey,
    setReference,
    setFieldMandatory,
    generateExampleData
  }
}

export default useEntityActions
